import Button from 'components/Button';
import {FC} from 'react';
import {Controller, UseFieldArrayReturn} from 'react-hook-form';

import BaseDropdown from '../Base/BaseDropdown';
import {approverRow, indexLabel} from './styles.module.css';
import {ApprovalGroup} from './types';
import {parser, validator} from './utils';

const ApproverEntry: FC<
    {
        index: number;
        name: string;
        options: ApprovalGroup[];
        required?: boolean;
        showButtons: boolean;
    } & UseFieldArrayReturn<Record<string, {value: null | string}[]>>
> = ({
    fields,
    index,
    name,
    options = [],
    remove,
    required,
    showButtons,
    swap,
    update,
}) => {
    const displayIndex = index + 1;

    const _options = parser(options).options();
    return (
        <Controller
            defaultValue={null}
            name={`${name}.${index}.value`}
            render={({field: {value}, fieldState: {error}}) => (
                <div className="flex flex-col" id={`approver-${displayIndex}`}>
                    <div
                        className={approverRow}
                        data-error={error ? 'true' : 'false'}
                        data-show-buttons={showButtons}
                    >
                        <div className={indexLabel}>{displayIndex}</div>
                        <BaseDropdown
                            className="flex-1"
                            onChange={(value) => {
                                if (!value) throw new Error('Invalid value');
                                update(index, {value});
                            }}
                            options={_options}
                            type="select"
                            value={value ? value : undefined}
                        />
                        {showButtons && (
                            <>
                                <Button
                                    data-testid="move-up"
                                    disabled={index === 0}
                                    icon="arrow_up"
                                    kind="secondary"
                                    onClick={
                                        index !== 0
                                            ? () => swap(index, index - 1)
                                            : undefined
                                    }
                                />
                                <Button
                                    data-testid="move-down"
                                    disabled={index === fields.length - 1}
                                    icon="arrow_down"
                                    kind="secondary"
                                    onClick={
                                        index !== fields.length - 1
                                            ? () => swap(index, index + 1)
                                            : undefined
                                    }
                                />
                                <Button
                                    data-testid="delete"
                                    icon="trash"
                                    kind="secondary"
                                    onClick={() => remove(index)}
                                />
                            </>
                        )}
                    </div>
                    {error && (
                        <div className="text-sm text-coral-500" role="alert">
                            {error.message}
                        </div>
                    )}
                </div>
            )}
            rules={{
                validate: (value) => {
                    if (!required) return undefined;

                    const allValues = fields.map((field) => field.value);

                    const validators = validator(value);

                    const isDefinedError = validators.isDefined();
                    if (isDefinedError) return isDefinedError;

                    const isRevokedUserError =
                        validators.isUsingRevokedUser(options);
                    if (isRevokedUserError) return isRevokedUserError;

                    const isSameApproverSeveralTimesInARowError =
                        validators.hasSameApproverSeveralTimesInARow(
                            index,
                            allValues,
                        );
                    if (isSameApproverSeveralTimesInARowError)
                        return isSameApproverSeveralTimesInARowError;

                    return undefined;
                },
            }}
        />
    );
};
export default ApproverEntry;
